import {
  IBlattAbteilung2Entry,
  IBlattAbteilung3Entry,
  IBlattFlurstueckNeu,
  IBlattGrundstueck,
  IBlattOwner,
  IGrundstueckAddress,
} from '../hooks/reactQuerys/GrundbuchblattQueries';

/**
 * Abgleich eines ausgelesenen Grundbuchauszugs mit einem **vorhandenen**
 * Grundbuchblatt (#1336).
 *
 * Warum es diesen Abgleich gibt: ein Blatt, das in DiNo schon steht, trägt
 * gepflegte Verknüpfungen — Mandanten, Adressen, Vorgänge. Eine frische
 * Auslesung darf das nicht ersetzen; sie ist eine Quelle, kein Ersatz. Bis
 * hierher wurde der Entwurf bei einem vorhandenen Blatt deshalb einfach
 * **verworfen**, und der Hinweis „bitte gleichen Sie ab" lief ins Leere: es gab
 * nichts mehr, womit man hätte abgleichen können.
 *
 * Zwei Regeln bestimmen alles Weitere:
 *
 * 1. **Der Auszug kann ergänzen und ändern, aber niemals leeren.** Eine
 *    Auslesung ist verlustbehaftet — ein Feld, das sie nicht gefunden hat, ist
 *    kein Beleg dafür, dass es leer sein soll. Nur Felder, in denen der Auszug
 *    tatsächlich etwas stehen hat, werden überhaupt verglichen.
 * 2. **Nichts wird von selbst übernommen.** Jede Abweichung ist ein eigener
 *    Vorschlag mit eigener Schaltfläche. Was im Auszug fehlt, aber in DiNo
 *    steht, wird nur gemeldet und hat gar keine Schaltfläche: einen Eintrag zu
 *    löschen, weil eine Texterkennung ihn übersehen hat, wäre der schlimmste
 *    Ausgang dieses Wegs.
 *
 * Die Abweichungsliste wird aus dem **aktuellen** Stand des Editors berechnet,
 * nicht einmalig beim Öffnen. Eine übernommene Abweichung verschwindet daher
 * von selbst, und wer einen Wert von Hand zurücksetzt, sieht sie wieder.
 */

/**
 * Ein ausgelesener Grundbuchauszug — dieselbe Form wie das fullObject, nur ohne
 * ids. Die vergibt der Editor beim Übernehmen selbst.
 */
export interface IBlattDraft {
  Blattnummer?: string | null;
  Blattart?: string | null;
  Aufschriftvermerke?: string | null;
  idCourt?: number | null;
  idAdministrativeUnit?: number | null;
  GemarkungFreitext?: string | null;
  GrundstueckArray?: unknown[];
  /**
   * Abteilung I. Kommt erst, nachdem der Mensch je Eigentümer entschieden hat,
   * ob zusammengeführt, neu angelegt oder ignoriert wird — die Einträge tragen
   * dann echte `idLegalClient`.
   */
  OwnerArray?: unknown[];
}

/** Der Stand im Editor, soweit der Abgleich ihn betrifft. */
export interface IBlattStand {
  Blattnummer: string;
  Blattart: string;
  Aufschriftvermerke: string;
  GemarkungFreitext: string;
  idCourt: number | '';
  idAdministrativeUnit: number | '';
  GrundstueckArray: IBlattGrundstueck[];
  OwnerArray: IBlattOwner[];
}

export type IAbweichungArt = 'feld' | 'neu' | 'fehlt';

/** Wohin die Abweichung im Grundbuch gehört — zugleich die Gruppierung. */
export type IBereich =
  | 'Aufschrift'
  | 'Bestandsverzeichnis'
  | 'Abteilung I'
  | 'Abteilung II'
  | 'Abteilung III';

/**
 * Was zu tun wäre. Bewusst nur Verweise (Schlüssel) statt fertiger Objekte:
 * die Daten holt `anwenden` beim Klick erneut aus dem Auszug. So bleibt die
 * Liste klein und die Übernahme hat genau eine Quelle.
 */
export type IPatch =
  | { Art: 'kopf'; Feld: keyof IBlattStand; Wert: unknown }
  | { Art: 'grundstueckFeld'; Grundstueck: string; Feld: string; Wert: unknown }
  | { Art: 'grundstueckNeu'; Grundstueck: string }
  | {
      Art: 'flurstueckFeld';
      Grundstueck: string;
      Flurstueck: string;
      Feld: string;
      Wert: unknown;
    }
  | { Art: 'flurstueckNeu'; Grundstueck: string; Flurstueck: string }
  | { Art: 'adresseNeu'; Grundstueck: string; Adresse: string }
  | {
      Art: 'abteilungFeld';
      Abteilung: 2 | 3;
      Grundstueck: string;
      Eintrag: string;
      Feld: string;
      Wert: unknown;
    }
  | {
      Art: 'abteilungNeu';
      Abteilung: 2 | 3;
      Grundstueck: string;
      Eintrag: string;
    }
  | { Art: 'ownerFeld'; Owner: string; Feld: string; Wert: unknown }
  | { Art: 'ownerNeu'; Owner: string };

export interface IAbweichung {
  /** Stabil über Neuberechnungen hinweg — React-Key und Testanker. */
  id: string;
  Bereich: IBereich;
  /** Wo im Blatt, in Grundbuchsprache: „Grundstück 2 · Flurstück 115/3". */
  Titel: string;
  Art: IAbweichungArt;
  /** Bei `Art: 'feld'` das betroffene Feld, sonst null. */
  Feld: string | null;
  /** Wie es in DiNo steht. */
  Alt: string | null;
  /** Wie es im Auszug steht. */
  Neu: string | null;
  /** null = nur Meldung, nicht übernehmbar (`Art: 'fehlt'`). */
  Patch: IPatch | null;
}

//////////////////// Vergleichswerte

/**
 * Einen Wert auf seine vergleichbare Form bringen. Leer ist leer: `null`,
 * `undefined` und der leere String sind dasselbe, und `0`/`false` ebenso —
 * die Historisierungs-Flags kommen je nach Weg als Zahl oder als Boolean.
 */
export const alsText = (wert: unknown): string | null => {
  if (wert === null || wert === undefined) {
    return null;
  }

  if (typeof wert === 'boolean') {
    return wert ? '1' : '0';
  }

  if (typeof wert === 'number') {
    return Number.isFinite(wert) ? String(wert) : null;
  }

  const text = String(wert).trim();

  return text === '' ? null : text;
};

/** Schlüssel zum Zusammenfinden: Groß-/Kleinschreibung und Leerraum egal. */
const alsSchluessel = (wert: unknown): string =>
  (alsText(wert) ?? '').toLowerCase().replace(/\s+/g, '');

const lies = (objekt: unknown, feld: string): unknown =>
  (objekt as Record<string, unknown> | null | undefined)?.[feld];

//////////////////// Felder, die verglichen werden

interface IFeld {
  key: string;
  Label: string;
}

const KOPF_FELDER: {
  key: keyof IBlattStand;
  Feld: keyof IBlattDraft;
  Label: string;
}[] = [
  { key: 'Blattnummer', Feld: 'Blattnummer', Label: 'Blattnummer' },
  { key: 'Blattart', Feld: 'Blattart', Label: 'Blattart' },
  {
    key: 'Aufschriftvermerke',
    Feld: 'Aufschriftvermerke',
    Label: 'Aufschriftvermerke',
  },
  { key: 'idCourt', Feld: 'idCourt', Label: 'Amtsgericht' },
  {
    key: 'idAdministrativeUnit',
    Feld: 'idAdministrativeUnit',
    Label: 'Gemarkung',
  },
  {
    key: 'GemarkungFreitext',
    Feld: 'GemarkungFreitext',
    Label: 'Gemarkung (Freitext)',
  },
];

const GRUNDSTUECK_FELDER: IFeld[] = [
  { key: 'bisherigeLfdNr', Label: 'Bisherige lfd. Nr.' },
  { key: 'istWohnungseigentum', Label: 'Wohnungs-/Teileigentum' },
  { key: 'MiteigentumsanteilZaehler', Label: 'Miteigentumsanteil (Zähler)' },
  { key: 'MiteigentumsanteilNenner', Label: 'Miteigentumsanteil (Nenner)' },
  { key: 'SondereigentumText', Label: 'Sondereigentum' },
  { key: 'AufteilungsplanNr', Label: 'Aufteilungsplan Nr.' },
  { key: 'TeilungserklaerungBezug', Label: 'Teilungserklärung' },
  { key: 'Veraeusserungsbeschraenkung', Label: 'Veräußerungsbeschränkung' },
  { key: 'HerrschendeRechte', Label: 'Herrschende Rechte' },
  { key: 'Bestandsvermerke', Label: 'Bestand und Zuschreibungen' },
  { key: 'BestandsvermerkeRechtspfleger', Label: 'Bestand: Rechtspfleger' },
  { key: 'Abschreibungen', Label: 'Abschreibungen' },
  {
    key: 'AbschreibungenRechtspfleger',
    Label: 'Abschreibungen: Rechtspfleger',
  },
  { key: 'istErbbaurecht', Label: 'Erbbaurecht' },
  { key: 'ErbbauzinsText', Label: 'Erbbauzins' },
  { key: 'ErbbaurechtLaufzeitBis', Label: 'Erbbaurecht bis' },
  { key: 'ErbbaurechtBezug', Label: 'Erbbaurecht: Bezug' },
];

/**
 * Flur und Flurstücksnummer fehlen hier mit Absicht: sie sind der Schlüssel,
 * über den beide Seiten zusammenfinden. Die Gemarkung ebenso — sie steht am
 * Blattkopf, nicht an der Zeile.
 */
const FLURSTUECK_FELDER: IFeld[] = [
  { key: 'Wirtschaftsart', Label: 'Wirtschaftsart' },
  { key: 'Lage', Label: 'Lage' },
  { key: 'Groesse', Label: 'Größe' },
];

const ABT2_FELDER: IFeld[] = [
  { key: 'Belastungsart', Label: 'Art der Belastung' },
  { key: 'Berechtigter', Label: 'Berechtigter' },
  { key: 'Inhalt', Label: 'Inhalt' },
  { key: 'Betrag', Label: 'Betrag' },
  { key: 'Waehrung', Label: 'Währung' },
  { key: 'bewilligtAm', Label: 'Bewilligt am' },
  { key: 'eingetragenAm', Label: 'Eingetragen am' },
  { key: 'GrundlageDerEintragung', Label: 'Grundlage der Eintragung' },
  { key: 'isVormerkung', Label: 'Vormerkung' },
  { key: 'Volltext', Label: 'Wortlaut' },
  { key: 'Rangvermerk', Label: 'Rangvermerk' },
  { key: 'GesamthaftBlaetter', Label: 'Gesamthaft (Blätter)' },
  { key: 'Veraenderungen', Label: 'Veränderungen' },
  { key: 'istGeloescht', Label: 'Gelöscht' },
  { key: 'geloeschtAm', Label: 'Gelöscht am' },
  { key: 'Rechtspfleger', Label: 'Rechtspfleger' },
];

const ABT3_FELDER: IFeld[] = [
  { key: 'Belastungsart', Label: 'Art des Rechts' },
  { key: 'Glaeubiger', Label: 'Gläubiger' },
  { key: 'Betrag', Label: 'Betrag' },
  { key: 'Waehrung', Label: 'Währung' },
  { key: 'Zinssatz', Label: 'Zinssatz' },
  { key: 'Nebenleistung', Label: 'Nebenleistung' },
  { key: 'Inhalt', Label: 'Inhalt' },
  { key: 'bewilligtAm', Label: 'Bewilligt am' },
  { key: 'eingetragenAm', Label: 'Eingetragen am' },
  { key: 'GrundlageDerEintragung', Label: 'Grundlage der Eintragung' },
  { key: 'isOhneBrief', Label: 'Ohne Brief' },
  { key: 'isVollstreckbar', Label: 'Vollstreckbar' },
  { key: 'Volltext', Label: 'Wortlaut' },
  { key: 'Rangvermerk', Label: 'Rangvermerk' },
  { key: 'GesamthaftBlaetter', Label: 'Gesamthaft (Blätter)' },
  { key: 'Veraenderungen', Label: 'Veränderungen' },
  { key: 'istGeloescht', Label: 'Gelöscht' },
  { key: 'geloeschtAm', Label: 'Gelöscht am' },
  { key: 'Rechtspfleger', Label: 'Rechtspfleger' },
];

const OWNER_FELDER: IFeld[] = [
  { key: 'lfdNr', Label: 'Lfd. Nr.' },
  { key: 'GrundlageDerEintragung', Label: 'Grundlage der Eintragung' },
  { key: 'Gemeinschaftsverhaeltnis', Label: 'Gemeinschaftsverhältnis' },
  { key: 'AnteilText', Label: 'Anteil' },
  { key: 'eingetragenAm', Label: 'Eingetragen am' },
  { key: 'istGeloescht', Label: 'Gelöscht' },
  { key: 'geloeschtAm', Label: 'Gelöscht am' },
  { key: 'Rechtspfleger', Label: 'Rechtspfleger' },
];

//////////////////// Schlüssel und Beschriftungen

const grundstueckSchluessel = (gs: unknown, index: number): string =>
  alsSchluessel(lies(gs, 'lfdNr')) || String(index + 1);

const flurstueckSchluessel = (fs: unknown): string =>
  `${alsSchluessel(lies(fs, 'Flur'))}|${alsSchluessel(lies(fs, 'Flurstuecksnummer'))}`;

const adresseSchluessel = (ad: unknown): string =>
  [
    alsSchluessel(lies(ad, 'Street')),
    alsSchluessel(lies(ad, 'StreetNr')),
    alsSchluessel(lies(ad, 'ZipCode')),
  ].join('|');

const eintragSchluessel = (eintrag: unknown, index: number): string =>
  alsSchluessel(lies(eintrag, 'lfdNr')) || `#${index + 1}`;

const ownerSchluessel = (owner: unknown): string =>
  alsSchluessel(lies(owner, 'idLegalClient'));

const flurstueckText = (fs: unknown): string => {
  const flur = alsText(lies(fs, 'Flur'));
  const nummer = alsText(lies(fs, 'Flurstuecksnummer')) ?? '—';

  return flur === null
    ? `Flurstück ${nummer}`
    : `Flur ${flur}, Flurstück ${nummer}`;
};

const adresseText = (ad: unknown): string =>
  [
    [alsText(lies(ad, 'Street')), alsText(lies(ad, 'StreetNr'))]
      .filter(Boolean)
      .join(' '),
    [alsText(lies(ad, 'ZipCode')), alsText(lies(ad, 'Place'))]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(teil => teil !== '')
    .join(', ') || 'Adresse ohne Angaben';

const eintragText = (eintrag: unknown, abteilung: 2 | 3): string => {
  const betrag = alsText(lies(eintrag, 'Betrag'));
  const waehrung = alsText(lies(eintrag, 'Waehrung')) ?? 'EUR';

  return (
    [
      alsText(lies(eintrag, 'Belastungsart')),
      alsText(lies(eintrag, abteilung === 2 ? 'Berechtigter' : 'Glaeubiger')),
      betrag === null ? null : `${betrag} ${waehrung}`,
    ]
      .filter(Boolean)
      .join(' · ') || 'Eintrag ohne Angaben'
  );
};

const ownerText = (owner: unknown): string =>
  [alsText(lies(owner, 'FirstName')), alsText(lies(owner, 'LastName'))]
    .filter(Boolean)
    .join(' ') ||
  alsText(lies(owner, 'Name')) ||
  `Mandant ${alsText(lies(owner, 'idLegalClient')) ?? '?'}`;

const grundstueckText = (gs: unknown): string => {
  const flurstuecke =
    (lies(gs, 'FlurstueckArray') as unknown[] | undefined) ?? [];

  return flurstuecke.length === 0
    ? 'ohne Flurstück'
    : flurstuecke.map(flurstueckText).join('; ');
};

//////////////////// Auszug aufbereiten

interface IAuszugGrundstueck {
  schluessel: string;
  titel: string;
  daten: Record<string, unknown>;
}

/** Die Grundstücke des Auszugs mit ihren Schlüsseln — einmal für alle Wege. */
export const auszugGrundstuecke = (draft: IBlattDraft): IAuszugGrundstueck[] =>
  ((draft.GrundstueckArray ?? []) as Record<string, unknown>[]).map(
    (gs, index) => ({
      schluessel: grundstueckSchluessel(gs, index),
      titel: `Grundstück ${alsText(gs.lfdNr) ?? index + 1}`,
      daten: gs,
    })
  );

const liste = (objekt: unknown, feld: string): Record<string, unknown>[] =>
  ((lies(objekt, feld) as unknown[] | undefined) ?? []) as Record<
    string,
    unknown
  >[];

//////////////////// Vergleich

/**
 * Ein einzelnes Feld vergleichen. Gibt null zurück, wenn der Auszug nichts
 * hergibt oder beide gleich sind — der Auszug leert nie (siehe Regel 1).
 */
const feldAbweichung = (
  id: string,
  Bereich: IBereich,
  Titel: string,
  feld: IFeld,
  vorhanden: unknown,
  ausAuszug: unknown,
  patch: (wert: unknown) => IPatch
): IAbweichung | null => {
  const neu = alsText(lies(ausAuszug, feld.key));

  if (neu === null) {
    return null;
  }

  const alt = alsText(lies(vorhanden, feld.key));

  if (alt === neu) {
    return null;
  }

  return {
    id: `${id}:${feld.key}`,
    Bereich,
    Titel,
    Art: 'feld',
    Feld: feld.Label,
    Alt: alt,
    Neu: neu,
    Patch: patch(lies(ausAuszug, feld.key)),
  };
};

/**
 * Alle Abweichungen zwischen dem Stand im Editor und dem Auszug.
 *
 * Reihenfolge wie im Grundbuch: Aufschrift, Bestandsverzeichnis, Abteilung I
 * bis III. Innerhalb eines Bereichs erst das Neue, dann die Änderungen, zuletzt
 * das, was im Auszug fehlt.
 */
export const berechneAbweichungen = (
  stand: IBlattStand,
  draft: IBlattDraft | null
): IAbweichung[] => {
  if (draft === null) {
    return [];
  }

  const abweichungen: IAbweichung[] = [];

  //// Aufschrift
  KOPF_FELDER.forEach(feld => {
    const eintrag = feldAbweichung(
      'kopf',
      'Aufschrift',
      'Aufschrift',
      { key: feld.Feld, Label: feld.Label },
      { [feld.Feld]: stand[feld.key] === '' ? null : stand[feld.key] },
      draft,
      wert => ({ Art: 'kopf', Feld: feld.key, Wert: wert })
    );

    if (eintrag !== null) {
      abweichungen.push(eintrag);
    }
  });

  //// Bestandsverzeichnis samt Abteilung II und III (die hängen am Grundstück)
  const vorhandeneGrundstuecke = new Map(
    stand.GrundstueckArray.map((gs, index) => [
      grundstueckSchluessel(gs, index),
      gs,
    ])
  );

  auszugGrundstuecke(draft).forEach(({ schluessel, titel, daten }) => {
    const vorhanden = vorhandeneGrundstuecke.get(schluessel);

    if (vorhanden === undefined) {
      abweichungen.push({
        id: `gs:${schluessel}`,
        Bereich: 'Bestandsverzeichnis',
        Titel: titel,
        Art: 'neu',
        Feld: null,
        Alt: null,
        Neu: grundstueckText(daten),
        Patch: { Art: 'grundstueckNeu', Grundstueck: schluessel },
      });

      return;
    }

    GRUNDSTUECK_FELDER.forEach(feld => {
      const eintrag = feldAbweichung(
        `gs:${schluessel}`,
        'Bestandsverzeichnis',
        titel,
        feld,
        vorhanden,
        daten,
        wert => ({
          Art: 'grundstueckFeld',
          Grundstueck: schluessel,
          Feld: feld.key,
          Wert: wert,
        })
      );

      if (eintrag !== null) {
        abweichungen.push(eintrag);
      }
    });

    //// Flurstücke
    const vorhandeneFlurstuecke = new Map(
      (vorhanden.FlurstueckArray ?? []).map(fs => [
        flurstueckSchluessel(fs),
        fs,
      ])
    );

    liste(daten, 'FlurstueckArray').forEach(fs => {
      const fsSchluessel = flurstueckSchluessel(fs);
      const fsVorhanden = vorhandeneFlurstuecke.get(fsSchluessel);
      const fsTitel = `${titel} · ${flurstueckText(fs)}`;

      if (fsVorhanden === undefined) {
        abweichungen.push({
          id: `fs:${schluessel}:${fsSchluessel}`,
          Bereich: 'Bestandsverzeichnis',
          Titel: titel,
          Art: 'neu',
          Feld: null,
          Alt: null,
          Neu: flurstueckText(fs),
          Patch: {
            Art: 'flurstueckNeu',
            Grundstueck: schluessel,
            Flurstueck: fsSchluessel,
          },
        });

        return;
      }

      FLURSTUECK_FELDER.forEach(feld => {
        const eintrag = feldAbweichung(
          `fs:${schluessel}:${fsSchluessel}`,
          'Bestandsverzeichnis',
          fsTitel,
          feld,
          fsVorhanden,
          fs,
          wert => ({
            Art: 'flurstueckFeld',
            Grundstueck: schluessel,
            Flurstueck: fsSchluessel,
            Feld: feld.key,
            Wert: wert,
          })
        );

        if (eintrag !== null) {
          abweichungen.push(eintrag);
        }
      });
    });

    //// Adressen — nur ergänzen, Feldvergleich lohnt bei drei Feldern nicht
    const vorhandeneAdressen = new Set(
      (vorhanden.AddressArray ?? []).map(adresseSchluessel)
    );

    liste(daten, 'AddressArray').forEach(ad => {
      const adSchluessel = adresseSchluessel(ad);

      if (vorhandeneAdressen.has(adSchluessel)) {
        return;
      }

      abweichungen.push({
        id: `ad:${schluessel}:${adSchluessel}`,
        Bereich: 'Bestandsverzeichnis',
        Titel: titel,
        Art: 'neu',
        Feld: null,
        Alt: null,
        Neu: adresseText(ad),
        Patch: {
          Art: 'adresseNeu',
          Grundstueck: schluessel,
          Adresse: adSchluessel,
        },
      });
    });

    //// Abteilung II und III
    ([2, 3] as const).forEach(abteilung => {
      const arrayName = abteilung === 2 ? 'Abteilung2Array' : 'Abteilung3Array';
      const felder = abteilung === 2 ? ABT2_FELDER : ABT3_FELDER;
      const bereich: IBereich =
        abteilung === 2 ? 'Abteilung II' : 'Abteilung III';
      const vorhandeneEintraege = new Map(
        ((vorhanden[arrayName] ?? []) as unknown[]).map((e, index) => [
          eintragSchluessel(e, index),
          e,
        ])
      );

      liste(daten, arrayName).forEach((eintrag, index) => {
        const eSchluessel = eintragSchluessel(eintrag, index);
        const eTitel = `${titel} · Nr. ${alsText(eintrag.lfdNr) ?? index + 1}`;
        const eVorhanden = vorhandeneEintraege.get(eSchluessel);

        if (eVorhanden === undefined) {
          abweichungen.push({
            id: `abt${abteilung}:${schluessel}:${eSchluessel}`,
            Bereich: bereich,
            Titel: eTitel,
            Art: 'neu',
            Feld: null,
            Alt: null,
            Neu: eintragText(eintrag, abteilung),
            Patch: {
              Art: 'abteilungNeu',
              Abteilung: abteilung,
              Grundstueck: schluessel,
              Eintrag: eSchluessel,
            },
          });

          return;
        }

        felder.forEach(feld => {
          const abweichung = feldAbweichung(
            `abt${abteilung}:${schluessel}:${eSchluessel}`,
            bereich,
            eTitel,
            feld,
            eVorhanden,
            eintrag,
            wert => ({
              Art: 'abteilungFeld',
              Abteilung: abteilung,
              Grundstueck: schluessel,
              Eintrag: eSchluessel,
              Feld: feld.key,
              Wert: wert,
            })
          );

          if (abweichung !== null) {
            abweichungen.push(abweichung);
          }
        });
      });

      //// Was in DiNo steht, aber im Auszug fehlt — nur melden
      const imAuszug = new Set(
        liste(daten, arrayName).map((e, index) => eintragSchluessel(e, index))
      );

      ((vorhanden[arrayName] ?? []) as unknown[]).forEach((e, index) => {
        const eSchluessel = eintragSchluessel(e, index);

        if (imAuszug.has(eSchluessel)) {
          return;
        }

        abweichungen.push({
          id: `abt${abteilung}fehlt:${schluessel}:${eSchluessel}`,
          Bereich: bereich,
          Titel: `${titel} · Nr. ${alsText(lies(e, 'lfdNr')) ?? index + 1}`,
          Art: 'fehlt',
          Feld: null,
          Alt: eintragText(e, abteilung),
          Neu: null,
          Patch: null,
        });
      });
    });
  });

  //// Grundstücke, die im Auszug fehlen
  const auszugSchluessel = new Set(
    auszugGrundstuecke(draft).map(g => g.schluessel)
  );

  stand.GrundstueckArray.forEach((gs, index) => {
    const schluessel = grundstueckSchluessel(gs, index);

    if (auszugSchluessel.has(schluessel)) {
      return;
    }

    abweichungen.push({
      id: `gsfehlt:${schluessel}`,
      Bereich: 'Bestandsverzeichnis',
      Titel: `Grundstück ${alsText(gs.lfdNr) ?? index + 1}`,
      Art: 'fehlt',
      Feld: null,
      Alt: grundstueckText(gs),
      Neu: null,
      Patch: null,
    });
  });

  //// Abteilung I
  const vorhandeneOwner = new Map(
    stand.OwnerArray.map(owner => [ownerSchluessel(owner), owner])
  );

  ((draft.OwnerArray ?? []) as Record<string, unknown>[]).forEach(owner => {
    const schluessel = ownerSchluessel(owner);

    // Ohne Mandanten-id lässt sich nichts zuordnen und nichts übernehmen —
    // solche Einträge hat der Mensch auf „nicht übernehmen" gestellt.
    if (schluessel === '' || schluessel === '0') {
      return;
    }

    const vorhanden = vorhandeneOwner.get(schluessel);

    if (vorhanden === undefined) {
      abweichungen.push({
        id: `owner:${schluessel}`,
        Bereich: 'Abteilung I',
        Titel: ownerText(owner),
        Art: 'neu',
        Feld: null,
        Alt: null,
        Neu: ownerText(owner),
        Patch: { Art: 'ownerNeu', Owner: schluessel },
      });

      return;
    }

    OWNER_FELDER.forEach(feld => {
      const eintrag = feldAbweichung(
        `owner:${schluessel}`,
        'Abteilung I',
        ownerText(vorhanden),
        feld,
        vorhanden,
        owner,
        wert => ({
          Art: 'ownerFeld',
          Owner: schluessel,
          Feld: feld.key,
          Wert: wert,
        })
      );

      if (eintrag !== null) {
        abweichungen.push(eintrag);
      }
    });
  });

  const ownerImAuszug = new Set(
    ((draft.OwnerArray ?? []) as unknown[]).map(ownerSchluessel)
  );

  stand.OwnerArray.forEach(owner => {
    const schluessel = ownerSchluessel(owner);

    if (ownerImAuszug.has(schluessel)) {
      return;
    }

    abweichungen.push({
      id: `ownerfehlt:${schluessel}`,
      Bereich: 'Abteilung I',
      Titel: ownerText(owner),
      Art: 'fehlt',
      Feld: null,
      Alt: ownerText(owner),
      Neu: null,
      Patch: null,
    });
  });

  return abweichungen;
};

//////////////////// Übernahme

const findeAuszugGrundstueck = (
  draft: IBlattDraft,
  schluessel: string
): Record<string, unknown> | null =>
  auszugGrundstuecke(draft).find(g => g.schluessel === schluessel)?.daten ??
  null;

/**
 * Eine Abweichung übernehmen. Gibt einen **neuen** Stand zurück; passt nichts,
 * kommt der alte unverändert zurück.
 *
 * `neueId` liefert die negativen Platzhalter-ids für noch nicht gespeicherte
 * Zeilen — dieselbe Quelle wie im Editor, damit sich zwei Zähler nicht ins
 * Gehege kommen.
 */
export const anwenden = (
  stand: IBlattStand,
  draft: IBlattDraft | null,
  patch: IPatch,
  neueId: () => number
): IBlattStand => {
  if (draft === null) {
    return stand;
  }

  if (patch.Art === 'kopf') {
    // Der Editor hält die Auswahlfelder als '' statt null.
    const wert =
      patch.Feld === 'idCourt' || patch.Feld === 'idAdministrativeUnit'
        ? ((patch.Wert as number | null) ?? '')
        : (alsText(patch.Wert) ?? '');

    return { ...stand, [patch.Feld]: wert };
  }

  if (patch.Art === 'ownerNeu') {
    const owner = ((draft.OwnerArray ?? []) as Record<string, unknown>[]).find(
      o => ownerSchluessel(o) === patch.Owner
    );

    return owner === undefined
      ? stand
      : {
          ...stand,
          OwnerArray: [...stand.OwnerArray, owner as unknown as IBlattOwner],
        };
  }

  if (patch.Art === 'ownerFeld') {
    return {
      ...stand,
      OwnerArray: stand.OwnerArray.map(owner =>
        ownerSchluessel(owner) === patch.Owner
          ? { ...owner, [patch.Feld]: patch.Wert }
          : owner
      ),
    };
  }

  const quelle = findeAuszugGrundstueck(draft, patch.Grundstueck);

  if (quelle === null) {
    return stand;
  }

  if (patch.Art === 'grundstueckNeu') {
    const neu: IBlattGrundstueck = {
      ...(quelle as unknown as IBlattGrundstueck),
      idGrundstueck: neueId(),
      lfdNr: alsText(quelle.lfdNr) ?? patch.Grundstueck,
      FlurstueckArray: liste(quelle, 'FlurstueckArray').map(fs => ({
        ...(fs as unknown as IBlattFlurstueckNeu),
        idFlurstueck: neueId(),
      })),
      AddressArray: liste(quelle, 'AddressArray').map(ad => ({
        ...(ad as unknown as IGrundstueckAddress),
        idGrundstueck_has_Address: neueId(),
      })),
      Abteilung2Array: liste(quelle, 'Abteilung2Array').map(e => ({
        ...(e as unknown as IBlattAbteilung2Entry),
        idGrundbuchAbteilung2: neueId(),
      })),
      Abteilung3Array: liste(quelle, 'Abteilung3Array').map(e => ({
        ...(e as unknown as IBlattAbteilung3Entry),
        idGrundbuchAbteilung3: neueId(),
      })),
    };

    return { ...stand, GrundstueckArray: [...stand.GrundstueckArray, neu] };
  }

  const aendere = (
    bearbeite: (gs: IBlattGrundstueck) => IBlattGrundstueck
  ): IBlattStand => ({
    ...stand,
    GrundstueckArray: stand.GrundstueckArray.map((gs, index) =>
      grundstueckSchluessel(gs, index) === patch.Grundstueck
        ? bearbeite(gs)
        : gs
    ),
  });

  if (patch.Art === 'grundstueckFeld') {
    return aendere(gs => ({ ...gs, [patch.Feld]: patch.Wert }));
  }

  if (patch.Art === 'flurstueckNeu') {
    const fs = liste(quelle, 'FlurstueckArray').find(
      f => flurstueckSchluessel(f) === patch.Flurstueck
    );

    return fs === undefined
      ? stand
      : aendere(gs => ({
          ...gs,
          FlurstueckArray: [
            ...(gs.FlurstueckArray ?? []),
            {
              ...(fs as unknown as IBlattFlurstueckNeu),
              idFlurstueck: neueId(),
            },
          ],
        }));
  }

  if (patch.Art === 'flurstueckFeld') {
    return aendere(gs => ({
      ...gs,
      FlurstueckArray: (gs.FlurstueckArray ?? []).map(fs =>
        flurstueckSchluessel(fs) === patch.Flurstueck
          ? { ...fs, [patch.Feld]: patch.Wert }
          : fs
      ),
    }));
  }

  if (patch.Art === 'adresseNeu') {
    const ad = liste(quelle, 'AddressArray').find(
      a => adresseSchluessel(a) === patch.Adresse
    );

    return ad === undefined
      ? stand
      : aendere(gs => ({
          ...gs,
          AddressArray: [
            ...(gs.AddressArray ?? []),
            {
              ...(ad as unknown as IGrundstueckAddress),
              idGrundstueck_has_Address: neueId(),
            },
          ],
        }));
  }

  const arrayName =
    patch.Abteilung === 2 ? 'Abteilung2Array' : 'Abteilung3Array';
  const idName =
    patch.Abteilung === 2 ? 'idGrundbuchAbteilung2' : 'idGrundbuchAbteilung3';

  if (patch.Art === 'abteilungNeu') {
    const eintrag = liste(quelle, arrayName).find(
      (e, index) => eintragSchluessel(e, index) === patch.Eintrag
    );

    return eintrag === undefined
      ? stand
      : aendere(gs => ({
          ...gs,
          [arrayName]: [
            ...(((gs as unknown as Record<string, unknown>)[arrayName] as
              | unknown[]
              | undefined) ?? []),
            { ...eintrag, [idName]: neueId() },
          ],
        }));
  }

  if (patch.Art === 'abteilungFeld') {
    return aendere(gs => ({
      ...gs,
      [arrayName]: (
        ((gs as unknown as Record<string, unknown>)[arrayName] as
          | unknown[]
          | undefined) ?? []
      ).map((e, index) =>
        eintragSchluessel(e, index) === patch.Eintrag
          ? { ...(e as object), [patch.Feld]: patch.Wert }
          : e
      ),
    }));
  }

  return stand;
};
